import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { ChartTheme } from './palette';

export type ValueFormatter = (value: number) => string;

interface Props extends TooltipProps<ValueType, NameType> {
  theme: ChartTheme;
  formatters?: Record<string, ValueFormatter>;
  defaultFormatter?: ValueFormatter;
  labelFormatter?: (label: string) => string;
}

/** Tooltip padronizado — sempre presente nos gráficos (camada de hover). */
export function ChartTooltip({
  active,
  payload,
  label,
  theme,
  formatters,
  defaultFormatter,
  labelFormatter,
}: Props) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-pop"
      style={{ background: theme.tooltipBg, borderColor: theme.tooltipBorder }}
    >
      <p className="mb-1.5 font-semibold text-fg">
        {labelFormatter ? labelFormatter(String(label ?? '')) : String(label ?? '')}
      </p>
      <ul className="space-y-1">
        {payload.map((entry) => {
          const key = String(entry.dataKey ?? entry.name ?? '');
          const format = formatters?.[key] ?? defaultFormatter ?? ((value: number) => String(value));
          const numeric = typeof entry.value === 'number' ? entry.value : Number(entry.value ?? 0);
          return (
            <li key={key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted">
                <span
                  aria-hidden
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: entry.color }}
                />
                {entry.name}
              </span>
              <span className="font-medium tabular-nums text-fg">{format(numeric)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
